import React, { useCallback, useEffect, useRef, useState } from 'react';

import { PrimaryButton, ProButton } from '@components/Button/variants';
import useEventTracking from '@hooks/useEventTracking';
import useScrollPosition from '@hooks/useScrollPosition';
import useWindowSize from '@hooks/useWindowSize';
import { subscribe_path } from '@routes';
import { ClickTarget, TrackingLocation, click } from '@tracking';

import OfferOption, { Offer, SelectedOffer } from '../OfferOption';

interface ContentProps {
  buttonText: string;
  headline: string;
  isPro: boolean;
  paywallLeadOffers?: Offer[];
  submitLink: string;
}

const Content = ({
  buttonText,
  headline,
  isPro: isProArticle,
  paywallLeadOffers,
  submitLink,
}: ContentProps) => {
  const footerElement = useRef<HTMLElement>(null);
  const [showBanner, setShowBanner] = useState<boolean>(true);
  const [selectedOption, setSelectedOption] = useState<SelectedOffer>(paywallLeadOffers[0]);
  const { height: windowHeight } = useWindowSize();
  const { Track, trackEvent } = useEventTracking({ location: TrackingLocation.BottomBanner });
  const withOffers = paywallLeadOffers.length > 0;
  const Button = (isProArticle && !withOffers) || selectedOption?.isPro ? ProButton : PrimaryButton;

  const updateBannerVisibility = useCallback(() => {
    const footerTop = footerElement.current?.offsetTop ?? Infinity;
    const scrollPosition = window.scrollY + windowHeight;
    setShowBanner(scrollPosition < footerTop);
  }, [windowHeight]);

  useScrollPosition({
    effect: () => updateBannerVisibility(),
    deps: [footerElement.current],
    wait: 100,
  });

  useEffect(() => {
    footerElement.current = document.getElementById('ti-footer');
    if (window.scrollY) updateBannerVisibility();
  }, [updateBannerVisibility]);

  const handleButtonClick = () => {
    if (withOffers) {
      const url = subscribe_path({ offer: selectedOption.code });
      trackEvent(
        click({ clickTarget: ClickTarget.Subscribe, clickText: buttonText, clickLink: url })
      );
      window.location.href = url;
    } else {
      trackEvent(
        click({ clickTarget: ClickTarget.Subscribe, clickText: buttonText, clickLink: submitLink })
      );
      window.location.href = submitLink;
    }
  };

  const handleOptionSelection = (offer: SelectedOffer) => {
    setSelectedOption(offer);
    trackEvent(click({ clickTarget: ClickTarget.Offer, offerCode: offer.code }));
  };

  return (
    <Track>
      {showBanner && (
        <div
          className="w-full bg-grayscale-charcoal px-16 py-16 sm:py-24"
          data-testid="known-lead-paywall"
        >
          <div className="m-auto flex flex-col items-start justify-center gap-16 sm:flex-row sm:gap-32 lg:gap-64">
            <div className="w-full sm:w-auto">
              <h1 className="my-0 max-w-full text-center font-suisse-works text-headline-xxs text-white sm:text-left sm:text-headline-md lg:text-headline-lg">
                {headline}
              </h1>
              {withOffers && (
                <div className="mt-16 flex flex-col items-center justify-start gap-16 sm:mt-8 sm:flex-row sm:items-start sm:gap-32">
                  {paywallLeadOffers.map((offer, index) => (
                    <OfferOption
                      key={index}
                      offer={offer}
                      selectedOfferCode={selectedOption.code}
                      setSelectedOffer={handleOptionSelection}
                    />
                  ))}
                </div>
              )}
            </div>
            <Button
              as="button"
              size="big"
              onClick={handleButtonClick}
              className="w-auto cursor-pointer touch-manipulation max-sm:w-auto sm:my-2 lg:my-[5px]"
            >
              {buttonText}
            </Button>
          </div>
        </div>
      )}
    </Track>
  );
};

export default Content;
